import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Input,
  model,
  output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from 'src/app/auth/auth.service';
import { MdViewerComponent } from 'src/app/modules/standalone/components/md-viewer/md-viewer.component';
import { LoaderComponent } from 'src/app/modules/shared/components/loader.component';
import { AutoResizeTextAreaDirective } from 'src/app/modules/shared/directives/auto-resize-text-area.directive';
import { AiAssistantError, AiAssistantPromptResponse } from 'src/app/modules/shared/models/ai-assistant.models';
import { AiAssistantService } from 'src/app/modules/shared/services/ai-assistant.service';
import { MaterialModule } from 'src/app/modules/shared/submodule-material.module';
import { CsTicketDialogComponent, HubSpotFormData } from './cs-ticket-dialog/cs-ticket-dialog.component';

type ChatMessage =
  | { type: 'user'; userMessage: string }
  | { type: 'assistant'; aiResponse: AiAssistantPromptResponse };

@Component({
  selector: 'app-sail-assistant',
  standalone: true,
  imports: [FormsModule, MaterialModule, LoaderComponent, AutoResizeTextAreaDirective, MdViewerComponent],
  templateUrl: './sail-assistant.component.html',
  styleUrl: './sail-assistant.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SailAssistantComponent {
  private aiAssistantService = inject(AiAssistantService);
  private dialog = inject(MatDialog);
  private authService = inject(AuthService);

  @Input() embedded = false;

  closed = output<void>();
  promptInput = model('');
  isSendingPrompt = signal(false);
  isSentFirstRequest = signal(false);
  assistantError = signal<AiAssistantError | null>(null);
  isContextLengthExceeded = computed(() => this.assistantError()?.code === 'context_length_exceeded');
  conversationId = signal<string | null>(null);
  chatMessages = signal<ChatMessage[]>([]);

  @ViewChild('sailBody') private sailBody!: ElementRef<HTMLDivElement>;
  @ViewChildren('userMessage') private userMessages!: QueryList<ElementRef>;

  onClose(): void {
    this.closed.emit();
  }

  onEnter(event: Event): void {
    event.preventDefault();
    this.onSendMessage();
  }

  async onSendMessage(): Promise<void> {
    if (this.promptInput().trim()) {
      const prompt = this.promptInput();
      this.chatMessages.update((messages) => [...messages, { type: 'user', userMessage: prompt }]);
      this.promptInput.set('');
      this.isSendingPrompt.set(true);
      this.isSentFirstRequest.set(true);
      setTimeout(() => this.scrollSailBodyToBottom(), 0);

      this.assistantError.set(null);

      try {
        const response = await this.getAssistantResponse(prompt);
        if (response.conversationId) {
          this.conversationId.set(response.conversationId);
        }

        if (response.error) {
          this.assistantError.set(response.error);
        } else {
          this.chatMessages.update((messages) => [...messages, { type: 'assistant', aiResponse: response }]);
        }
      } catch {
        this.assistantError.set({
          code: 'request_failed',
          message: 'Something went wrong while contacting the AI Assistant. Please try again.',
        });
      } finally {
        this.isSendingPrompt.set(false);
        setTimeout(() => this.scrollToLastUserMessage(), 0);
      }
    }
  }

  onRestartConversation(): void {
    this.chatMessages.set([]);
    this.assistantError.set(null);
    this.conversationId.set(null);
    this.promptInput.set('');
    this.isSentFirstRequest.set(false);
    setTimeout(() => this.scrollSailBodyToBottom(), 0);
  }

  async onOpenTicket(): Promise<void> {
    const user = await this.authService.getUser();
    const formData: HubSpotFormData = {};

    if (user) {
      formData.firstName = user.givenName ?? '';
      formData.lastName = user.familyName ?? '';
      formData.email = user.email ?? '';
    }

    this.dialog.open(CsTicketDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'cs-ticket-dialog',
      autoFocus: false,
      data: formData,
    });
  }

  private scrollSailBodyToBottom(): void {
    const el = this.sailBody.nativeElement;
    el.scrollTop = el.scrollHeight;
  }

  private getAssistantResponse(prompt: string): Promise<AiAssistantPromptResponse> {
    return this.aiAssistantService.runAiAssistantPrompt(prompt, this.conversationId());
  }

  private scrollToLastUserMessage() {
    const elements = this.userMessages.toArray();
    if (elements.length >= 1) {
      const secondLast = elements[elements.length - 1].nativeElement;
      secondLast.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
